import type { AnalyticDateFilter } from '~/presentation/ui';
import type { IPathAnalysisFirstLevel } from '../contexts';

import { buildCampaignPathsQueryVariables, extractCampaignPathsData } from '../helpers';
import { CAMPAIGN_PATHS_V2_QUERY_NAME } from '../constants';
import { EAnalyticMode } from '~/modules/analytics/types';
import { useGemxQlNamedQuery } from '~/core/gemxql/api';
import { useMemo } from 'react';

export interface IFetchCampaignPathsV2Props {
  currentDates: AnalyticDateFilter;
  mode?: EAnalyticMode;
  campaignVariantID?: string;
  campaignVersionID?: string;
  isEnabled?: boolean;
}

export interface IFetchCampaignPathsV2Result {
  firstLevelPaths: IPathAnalysisFirstLevel[] | undefined;
  isFetching: boolean;
}

/**
 * GemxQL replacement for `useFetchCampaignPaths`. Returns the identical contract
 * (`{ firstLevelPaths, isFetching }`) and `isEnabled` gating so `PathAnalysisProvider` can swap
 * to it with no other changes. No WebSocket cache — the legacy WS invalidation layer is dropped
 * (matches the sibling GemxQL migrations).
 */
export const useFetchCampaignPathsV2 = ({
  currentDates,
  mode,
  campaignVariantID,
  campaignVersionID,
  isEnabled,
}: IFetchCampaignPathsV2Props): IFetchCampaignPathsV2Result => {
  const isEnabledQueryFetch = !!campaignVariantID && !!mode && !!isEnabled;

  const variables = useMemo(
    () =>
      buildCampaignPathsQueryVariables({
        // Guarded by isEnabledQueryFetch below; fallback only satisfies the builder's types.
        mode: mode ?? EAnalyticMode.ALL_SESSION,
        campaignVariantID: campaignVariantID ?? '',
        campaignVersionID,
        dateRange: currentDates,
      }),
    [mode, campaignVariantID, campaignVersionID, currentDates],
  );

  const { data: response, isLoading } = useGemxQlNamedQuery(
    { name: CAMPAIGN_PATHS_V2_QUERY_NAME, variables },
    { enabled: isEnabledQueryFetch && !!variables.input },
  );

  const firstLevelPaths = useMemo(() => extractCampaignPathsData(response), [response]);

  return {
    firstLevelPaths,
    isFetching: isLoading && isEnabledQueryFetch,
  };
};
